package diana.soleil.design.iraniancanadienbazzar.core.data.firestore

import diana.soleil.design.iraniancanadienbazzar.core.common.AppError
import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.data.EventRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.isUpcomingAt
import diana.soleil.design.iraniancanadienbazzar.core.data.OrderRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.ProductRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.TicketRepository
import diana.soleil.design.iraniancanadienbazzar.core.firebase.AuthService
import diana.soleil.design.iraniancanadienbazzar.core.firebase.FirebaseServices
import diana.soleil.design.iraniancanadienbazzar.core.model.CartItem
import diana.soleil.design.iraniancanadienbazzar.core.model.LocalizedText
import diana.soleil.design.iraniancanadienbazzar.core.model.Order
import diana.soleil.design.iraniancanadienbazzar.core.model.OrderStatus
import diana.soleil.design.iraniancanadienbazzar.core.model.PaymentStatus
import diana.soleil.design.iraniancanadienbazzar.core.model.Event
import diana.soleil.design.iraniancanadienbazzar.core.model.Product
import diana.soleil.design.iraniancanadienbazzar.core.model.PropertyDetails
import diana.soleil.design.iraniancanadienbazzar.core.model.Ticket
import diana.soleil.design.iraniancanadienbazzar.core.model.TicketType
import dev.gitlive.firebase.firestore.Direction
import dev.gitlive.firebase.firestore.FirebaseFirestore
import dev.gitlive.firebase.firestore.Query
import dev.gitlive.firebase.firestore.Timestamp
import kotlinx.coroutines.withTimeout
import kotlin.time.Clock
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.map

/**
 * Commerce + events: products, pickup orders, event listings, free QR tickets.
 *
 * Split out of the former 2,753-line FirestoreRepositories.kt (2026-08-02) — same code,
 * same behaviour, one file per domain. Shared collection names and helpers live in
 * FirestoreCommon.kt.
 */

/**
 * Live [ProductRepository] over Cloud Firestore (GitLive) — `businesses/{id}/products` subcollection
 * (public read; owner/admin writes are rules-enforced).
 */
class FirestoreProductRepository(
    firebase: FirebaseServices,
) : ProductRepository {

    private val firestore: FirebaseFirestore = firebase.firestore

    override fun observeProducts(businessId: String): Flow<DataResult<List<Product>>> =
        firestore.collection(BUSINESSES).document(businessId).collection(PRODUCTS).snapshots
            .map { snap ->
                val products = snap.documents
                    .mapNotNull { doc -> runCatching { doc.decode<ProductDoc>().toModel(doc.id, businessId) }.getOrNull() }
                    .sortedBy { it.priceCents }
                DataResult.Success(products) as DataResult<List<Product>>
            }
            .catch { emit(DataResult.Failure(it.toAppError())) }

    override suspend fun upsertProduct(
        businessId: String,
        productId: String?,
        name: LocalizedText,
        description: LocalizedText,
        priceCents: Long,
        available: Boolean,
        promoted: Boolean,
        property: PropertyDetails?,
    ): DataResult<Unit> = try {
        val write = ProductWriteDoc(
            name = LocalizedTextDoc(en = name.en, fr = name.fr, fa = name.fa),
            description = LocalizedTextDoc(en = description.en, fr = description.fr, fa = description.fa),
            priceCents = priceCents,
            available = available,
            promoted = promoted,
            property = property?.takeIf { it.hasAnyFact }?.toDoc(),
        )
        val col = firestore.collection(BUSINESSES).document(businessId).collection(PRODUCTS)
        if (productId == null) col.add(write) else col.document(productId).set(write, merge = true)
        DataResult.Success(Unit)
    } catch (t: Throwable) {
        DataResult.Failure(t.toAppError())
    }
}

/**
 * Live [OrderRepository] over Cloud Firestore (GitLive). Create is buyer-authored + forced
 * pending/unpaid (rules); totals are integer cents computed client-side (equal to subtotal until a
 * real payment/tax provider exists — no fake estimates).
 */
class FirestoreOrderRepository(
    firebase: FirebaseServices,
    private val auth: AuthService,
) : OrderRepository {

    private val firestore: FirebaseFirestore = firebase.firestore

    override suspend fun placeOrder(items: List<CartItem>, note: String): DataResult<String> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        val businessId = items.firstOrNull()?.businessId
            ?: return DataResult.Failure(AppError.Unknown())
        if (items.any { it.businessId != businessId }) return DataResult.Failure(AppError.Unknown())
        return try {
            val subtotal = items.sumOf { it.lineTotalCents }
            val ref = withTimeout(WRITE_TIMEOUT_MS) { firestore.collection(ORDERS).add(
                OrderCreateDoc(
                    buyerUid = user.uid,
                    businessId = businessId,
                    businessName = items.first().businessName,
                    items = items.map { OrderItemDoc(it.productId, it.name, it.quantity, it.priceCents) },
                    subtotalCents = subtotal,
                    totalCents = subtotal,
                    status = "pending",
                    paymentStatus = "unpaid",
                    note = note.trim(),
                    createdAt = Timestamp.ServerTimestamp,
                ),
            ) }
            DataResult.Success(ref.id)
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }

    @OptIn(ExperimentalCoroutinesApi::class)
    override fun observeMyOrders(): Flow<DataResult<List<Order>>> =
        auth.authState.flatMapLatest { user ->
            if (user == null || user.isAnonymous) {
                kotlinx.coroutines.flow.flowOf(DataResult.Failure(AppError.Unauthorized()))
            } else {
                firestore.collection(ORDERS).where { "buyerUid" equalTo user.uid }.snapshots
                    .map { snap ->
                        val orders = snap.documents
                            .mapNotNull { doc -> runCatching { doc.decode<OrderDoc>().toModel(doc.id) }.getOrNull() }
                            .sortedByDescending { it.createdAtMillis ?: 0L }
                        DataResult.Success(orders) as DataResult<List<Order>>
                    }
                    .catch { emit(DataResult.Failure(it.toAppError())) }
            }
        }

    override fun observeBusinessOrders(businessId: String): Flow<DataResult<List<Order>>> =
        auth.authState.flatMapLatest { user ->
            if (user == null || user.isAnonymous) {
                kotlinx.coroutines.flow.flowOf(DataResult.Failure(AppError.Unauthorized()))
            } else {
                // Server-ordered + bounded (composite index businessId+createdAt in
                // firestore.indexes.json); rules restrict the read to the business owner/admin.
                firestore.collection(ORDERS)
                    .where { "businessId" equalTo businessId }
                    .orderBy("createdAt", Direction.DESCENDING)
                    .limit(200)
                    .snapshots
                    .map { snap ->
                        val orders = snap.documents
                            .mapNotNull { doc -> runCatching { doc.decode<OrderDoc>().toModel(doc.id) }.getOrNull() }
                        DataResult.Success(orders) as DataResult<List<Order>>
                    }
                    .catch { emit(DataResult.Failure(it.toAppError())) }
            }
        }

    override suspend fun updateOrderStatus(
        orderId: String,
        status: OrderStatus?,
        paymentStatus: PaymentStatus?,
    ): DataResult<Unit> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        val fields = buildList {
            status?.let { add("status" to it.wire) }
            paymentStatus?.let { add("paymentStatus" to it.wire) }
        }
        if (fields.isEmpty()) return DataResult.Success(Unit)
        return try {
            withTimeout(WRITE_TIMEOUT_MS) {
                firestore.collection(ORDERS).document(orderId).update(*fields.toTypedArray())
            }
            DataResult.Success(Unit)
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }

    override suspend fun cancelMyOrder(orderId: String): DataResult<Unit> =
        updateOrderStatus(orderId, status = OrderStatus.Cancelled)

    private val OrderStatus.wire: String
        get() = when (this) {
            OrderStatus.Pending -> "pending"
            OrderStatus.Confirmed -> "confirmed"
            OrderStatus.Fulfilled -> "fulfilled"
            OrderStatus.Cancelled -> "cancelled"
        }

    private val PaymentStatus.wire: String
        get() = when (this) {
            PaymentStatus.Unpaid -> "unpaid"
            PaymentStatus.Paid -> "paid"
            PaymentStatus.Refunded -> "refunded"
        }

    override suspend fun getOrder(orderId: String): DataResult<Order> = try {
        val snapshot = firestore.collection(ORDERS).document(orderId).get()
        if (!snapshot.exists) DataResult.Failure(AppError.NotFound())
        else DataResult.Success(snapshot.decode<OrderDoc>().toModel(snapshot.id))
    } catch (t: Throwable) {
        DataResult.Failure(t.toAppError())
    }
}

/**
 * How far back the server-side `startsAt` floor reaches. Wide enough to still return an event that
 * began earlier today (in progress, or all-day) so the precise client-side end check can judge it;
 * everything older never leaves Firestore.
 */
private const val UPCOMING_QUERY_GRACE_MILLIS = 24L * 60 * 60 * 1_000

/** Approved event listings; tickets always open the organizer's external URL in Phase 3. */
class FirestoreEventRepository(
    firebase: FirebaseServices,
    private val auth: AuthService,
) : EventRepository {
    private val firestore: FirebaseFirestore = firebase.firestore

    /**
     * Approved events that have not finished yet, soonest first.
     *
     * This used to return EVERY approved event ordered by `startsAt` ascending — which, because the
     * order is ascending, put the most stale ones FIRST. Months-old events led the Home rail and the
     * Events screen, and `limit(100)` was spent on the past before it ever reached anything upcoming.
     *
     * Filtered in two stages, because Firestore can only range-filter comfortably on the field it
     * orders by:
     *  - **Server:** `startsAt >= now - [UPCOMING_QUERY_GRACE_MILLIS]`. The grace keeps an event that
     *    started earlier today (in progress, or an all-day event) instead of dropping it the moment it
     *    begins. Needs no new index — a range on the `orderBy` field reuses the existing
     *    (status, startsAt) / (status, metroId, startsAt) composites.
     *  - **Client:** [isUpcomingAt] drops anything whose end has passed. `now` is read per snapshot,
     *    so a long-lived subscription keeps ageing events out rather than freezing the cutoff at
     *    subscribe time.
     */
    override fun observeUpcomingEvents(metroId: String?): Flow<DataResult<List<Event>>> {
        val floorMillis = Clock.System.now().toEpochMilliseconds() - UPCOMING_QUERY_GRACE_MILLIS
        var query: Query = firestore.collection(EVENTS).where { "status" equalTo STATUS_APPROVED }
        if (metroId != null) query = query.where { "metroId" equalTo metroId }
        query = query.where { "startsAt" greaterThanOrEqualTo Timestamp(floorMillis / 1_000, 0) }
        return query.orderBy("startsAt", Direction.ASCENDING).limit(100).snapshots
            .map { snap ->
                val nowMillis = Clock.System.now().toEpochMilliseconds()
                val events = snap.documents
                    .mapNotNull { doc -> runCatching { doc.decode<EventDoc>().toModel(doc.id) }.getOrNull() }
                    .filter { it.isUpcomingAt(nowMillis) }
                    .sortedBy { it.startsAtMillis }
                DataResult.Success(events) as DataResult<List<Event>>
            }
            .catch { emit(DataResult.Failure(it.toAppError())) }
    }

    override suspend fun getEvent(eventId: String): DataResult<Event> = try {
        val snapshot = firestore.collection(EVENTS).document(eventId).get()
        val event = if (snapshot.exists) snapshot.decode<EventDoc>().toModel(snapshot.id) else null
        if (event == null) DataResult.Failure(AppError.NotFound()) else DataResult.Success(event)
    } catch (t: Throwable) {
        DataResult.Failure(t.toAppError())
    }

    @OptIn(ExperimentalCoroutinesApi::class)
    override fun observeMyRsvps(): Flow<Set<String>> =
        auth.authState.flatMapLatest { user ->
            if (user == null || user.isAnonymous) {
                flowOf(emptySet())
            } else {
                firestore.collection(USERS).document(user.uid).collection(RSVPS).snapshots
                    .map { snap -> snap.documents.map { it.id }.toSet() }
                    .catch { emit(emptySet()) }
            }
        }

    override suspend fun setRsvp(eventId: String, going: Boolean): DataResult<Unit> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        return try {
            // Dual write: public count mark (events/{id}/rsvps/{uid}) + private mirror
            // (users/{uid}/rsvps/{eventId}) so "my going" state is ONE listener, not N.
            val publicMark = firestore.collection(EVENTS).document(eventId).collection(RSVPS).document(user.uid)
            val mirror = firestore.collection(USERS).document(user.uid).collection(RSVPS).document(eventId)
            val batch = firestore.batch()
            if (going) {
                batch.set(publicMark, RsvpCreateDoc(uid = user.uid, eventId = eventId, createdAt = Timestamp.ServerTimestamp))
                batch.set(mirror, RsvpMirrorDoc(goingAt = Timestamp.ServerTimestamp))
            } else {
                batch.delete(publicMark)
                batch.delete(mirror)
            }
            batch.commit()
            DataResult.Success(Unit)
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }

    override suspend fun rsvpCounts(eventIds: Collection<String>): Map<String, Int> =
        eventIds.associateWith { id ->
            runCatching { firestore.collection(EVENTS).document(id).collection(RSVPS).get().documents.size }
                .getOrDefault(0)
        }
}

/**
 * Live [TicketRepository]: reads over Firestore, issuance via the region-pinned issueTicket callable
 * (server-side transaction — the ONLY writer of `tickets/`).
 */
class FirestoreTicketRepository(
    private val firebase: FirebaseServices,
    private val auth: AuthService,
) : TicketRepository {

    private val firestore: FirebaseFirestore get() = firebase.firestore

    override fun observeTicketTypes(eventId: String): Flow<DataResult<List<TicketType>>> =
        firestore.collection(EVENTS).document(eventId).collection(TICKET_TYPES).snapshots
            .map { snap ->
                val types = snap.documents
                    .mapNotNull { doc -> runCatching { doc.decode<TicketTypeDoc>().toModel(doc.id) }.getOrNull() }
                    .sortedBy { it.priceCents }
                DataResult.Success(types) as DataResult<List<TicketType>>
            }
            .catch { emit(DataResult.Failure(it.toAppError())) }

    @OptIn(ExperimentalCoroutinesApi::class)
    override fun observeMyTickets(): Flow<DataResult<List<Ticket>>> =
        auth.authState.flatMapLatest { user ->
            if (user == null || user.isAnonymous) {
                kotlinx.coroutines.flow.flowOf(DataResult.Failure(AppError.Unauthorized()))
            } else {
                firestore.collection(TICKETS).where { "buyerUid" equalTo user.uid }.snapshots
                    .map { snap ->
                        val tickets = snap.documents
                            .mapNotNull { doc -> runCatching { doc.decode<TicketDoc>().toModel(doc.id) }.getOrNull() }
                            .sortedByDescending { it.purchasedAtMillis ?: 0L }
                        DataResult.Success(tickets) as DataResult<List<Ticket>>
                    }
                    .catch { emit(DataResult.Failure(it.toAppError())) }
            }
        }

    override suspend fun issueFreeTicket(eventId: String, ticketTypeId: String, qty: Int): DataResult<String> {
        val user = auth.currentUser
        if (user == null || user.isAnonymous) return DataResult.Failure(AppError.Unauthorized())
        return try {
            val result = firebase.functions.httpsCallable("issueTicket")
                .invoke(mapOf("eventId" to eventId, "ticketTypeId" to ticketTypeId, "qty" to qty))
            val ticketId = result.data<Map<String, Any?>>()["ticketId"]?.toString()
                ?: return DataResult.Failure(AppError.Unknown())
            DataResult.Success(ticketId)
        } catch (t: Throwable) {
            DataResult.Failure(t.toAppError())
        }
    }
}
